import { t } from "i18next"
import { capitalizeFirstLetter, convertCamelCaseToReadable } from "utils"
import { getURLString, getUserMappingKey } from "utils/reports"

const getUserData = (item, parentId) => {
  let obj
  if (item?.data) {
    obj = {
      ...item.data,
      id: item.data.userId,
      name: item.data.name,
      nameAr: item.data.nameAr,
      reporteesCount: item.data.totalMembers + " " + t("Members"),
      designation: item.data.designation,
      parentId: parentId,
      ...(item?.children && { children: item.children.map(e => getUserData(e, parentId)) }),
    }
  } else {
    obj = {
      ...item,
      type: "region",
      id: item?.cities ? "region-" + item?.regionId : item?.cityId,
      name: item?.cityName || item.regionName,
      parentId: parentId,
      ...(item?.cities && {
        children: item.cities.map(e => getUserData(e, item?.cities ? "region-" + item?.regionId : item?.cityId)),
      }),
    }
  }
  return obj
}

const getNestedUserData = items => {
  return items?.length <= 1 ? (!!items?.[0]?.children?.length ? getNestedUserData(items?.[0]?.children) : items) : items
}

// Function to transform keys by prefixing them with parent names
const transformKeysWithParentPrefix = (userKpiData) => {
  const transformedData = { ...userKpiData }
  
  // Helper function to transform data object keys
  const transformDataKeys = (dataObj) => {
    if (!dataObj || typeof dataObj !== 'object') return dataObj
    
    const newDataObj = {}
    Object.keys(dataObj).forEach(parentKey => {
      const parentData = dataObj[parentKey]
      if (typeof parentData === 'object' && parentData !== null && !Array.isArray(parentData)) {
        const newParentData = {}
        Object.keys(parentData).forEach(childKey => {
          const newKey = `${parentKey}${childKey.charAt(0).toUpperCase() + childKey.slice(1)}`
          newParentData[newKey] = parentData[childKey]
        })
        newDataObj[parentKey] = newParentData
      } else {
        newDataObj[parentKey] = parentData
      }
    })
    return newDataObj
  }
  
  // Helper function to recursively transform data items
  const transformDataItem = (item) => {
    if (!item) return item
    
    const transformedItem = { ...item }
    
    // Transform the data property if it exists
    if (transformedItem.data) {
      transformedItem.data = transformDataKeys(transformedItem.data)
    }
    
    // Recursively transform children
    if (transformedItem.children && Array.isArray(transformedItem.children)) {
      transformedItem.children = transformedItem.children.map(child => transformDataItem(child))
    }
    
    return transformedItem
  }
  
  // Transform data array
  if (transformedData.data && Array.isArray(transformedData.data)) {
    transformedData.data = transformedData.data.map(item => transformDataItem(item))
  }
  
  // Transform staticKeys
  if (transformedData.staticKeys) {
    const newStaticKeys = {}
    Object.keys(transformedData.staticKeys).forEach(parentKey => {
      const parentStaticKeys = transformedData.staticKeys[parentKey]
      if (typeof parentStaticKeys === 'object' && parentStaticKeys !== null) {
        const newParentStaticKeys = {}
        Object.keys(parentStaticKeys).forEach(childKey => {
          if (childKey === 'order') {
            // Keep order key as is
            newParentStaticKeys[childKey] = parentStaticKeys[childKey]
          } else {
            const newKey = `${parentKey}${childKey.charAt(0).toUpperCase() + childKey.slice(1)}`
            newParentStaticKeys[newKey] = parentStaticKeys[childKey]
          }
        })
        newStaticKeys[parentKey] = newParentStaticKeys
      } else {
        newStaticKeys[parentKey] = parentStaticKeys
      }
    })
    transformedData.staticKeys = newStaticKeys
  }
  
  return transformedData
}

export const overviewTransformer = (response, req, params) => {
  // Transform the overview data to have unique keys
  const transformedOverview = transformKeysWithParentPrefix(response?.overview || {})
  const transformedResponse = {
    ...response,
    overview: transformedOverview
  }
  
  let listKeys = []
  let filteredColumns = ""

  const parentKeys = Object.keys(transformedResponse?.overview?.staticKeys || {})
    .map(parentKey => ({
      parentKey,
      order: transformedResponse.overview.staticKeys[parentKey]?.order ?? 0,
    }))
    .sort((a, b) => a.order - b.order)

  parentKeys.map(({ parentKey }) => {
    const children = Object.keys(transformedResponse?.overview?.staticKeys[parentKey] || {})
      .filter(childKey => childKey !== "order") // ignore order key
      .map(childKey => {
        const { order, ...childData } = transformedResponse.overview.staticKeys[parentKey][childKey] || {}
        if (childData?.default === true) {
          filteredColumns += (filteredColumns ? "," : "") + childKey
        }
        return {
          type: childData?.type ? childData.type : childData?.hover ? "hover" : "number",
          ...childData,
          key: childKey,
          slopeKey: `${childKey}Slope`,
          parent: parentKey,
          comparity: false, // Set comparity to false
          hoverKeys: childData?.hover ? childData?.hoverKeys : {},
          urlMapping: {
            url: getURLString(transformedResponse?.filters, params, childData, parentKey, childKey),
            userMappingKey: getUserMappingKey(childData),
          },
          target: childData?.target,
          order: order ?? 0,
        }
      })
      .sort((a, b) => a.order - b.order)
    listKeys.push({
      key: parentKey,
      label: t(capitalizeFirstLetter(convertCamelCaseToReadable(parentKey))),
      title: t(capitalizeFirstLetter(convertCamelCaseToReadable(parentKey))),
      children,
    })
  })

  const userKpi = {
    ...transformedResponse.overview,
    data: transformedResponse.overview.data.map(item => getUserData(item, item?.data?.userId || item?.regionId)),
  }

  return {
    ...transformedResponse,
    listKeys,
    userKpi,
    staffRegionComparison: { data: getNestedUserData(transformedResponse?.overview?.data.map(item => getUserData(item))) },
    defaultComparison: [
      { label: t("Qualified Calls"), short: "Q Calls", key: "qualifiedCalls", parent: "calls" },
      { label: t("Meetings Done"), short: "M D", key: "meetingsDone", parent: "meetings" },
    ],
    defaultFilters: { filteredColumns },
  }
}
